import { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, MoreVertical, Filter, ShoppingCart, X, ChevronDown, ChevronRight, ListPlus, Carrot, Apple, Milk, Fish, Wheat, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconBadge, type IconBadgeVariant } from "@/components/ui/IconBadge";
import { MemberSelectorButton } from "@/components/family/MemberSelectorButton";
import {
  useShoppingList,
  getSourceRecipesFromItem,
  type ProductCategory,
  type ShoppingListItemRow,
  type SourceRecipe,
  type ShoppingListSyncMeta,
} from "@/hooks/useShoppingList";
import { usePlanShoppingIngredients } from "@/hooks/usePlanShoppingIngredients";
import { useSubscription } from "@/hooks/useSubscription";
import { useFamily } from "@/contexts/FamilyContext";
import { mealPlanMemberIdForShoppingSync } from "@/utils/mealPlanMemberScope";
import { BuildShoppingListFromPlanSheet } from "@/components/plan/BuildShoppingListFromPlanSheet";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { formatShoppingListForCopy } from "@/utils/shoppingListTextFormatter";
import { normalizeIngredientDisplayName } from "@/utils/shopping/normalizeIngredientForShopping";
import { formatShoppingListPurchaseLine } from "@/utils/shopping/shoppingListPurchaseDisplay";
import { mapDbProductCategoryToShoppingAisle } from "@/utils/shopping/mapDbProductCategoryToShoppingAisle";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalizeIngredientName } from "@/utils/ingredientDisplay";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { SHOPPING_LIST_ENTRANCE_SESSION_KEY } from "@/utils/shopping/shoppingListEntrance";
import { computeEffectiveShoppingItemView } from "@/utils/shopping/shoppingListEffectiveView";
import { PANTRY_ASSUMPTION_USER_HINT_RU } from "@/utils/shopping/pantryStaplesShopping";

const CATEGORY_ORDER: ProductCategory[] = ["vegetables", "fruits", "dairy", "meat", "grains", "other"];
const CATEGORY_LABEL: Record<ProductCategory, string> = {
  vegetables: "Овощи",
  fruits: "Фрукты",
  dairy: "Молочное",
  meat: "Мясо и рыба",
  grains: "Крупы и злаки",
  other: "Прочее",
};

/** Иконка и вариант плашки для заголовка категории (premium icon system). */
const CATEGORY_ICON: Record<ProductCategory, import("react").ComponentType<{ className?: string }>> = {
  vegetables: Carrot,
  fruits: Apple,
  dairy: Milk,
  meat: Fish,
  grains: Wheat,
  other: Package,
};
const CATEGORY_BADGE_VARIANT: Record<ProductCategory, IconBadgeVariant> = {
  vegetables: "sage",
  fruits: "sage",
  dairy: "mint",
  meat: "apricot",
  grains: "sand",
  other: "amber",
};

function formatItemShort(
  item: ShoppingListItemRow,
  effective?: {
    amount: number | null;
    unit: string | null;
    scaledDualDisplayAmount: number | null;
    dualDisplayUnit: string | null;
  }
): string {
  const name = normalizeIngredientDisplayName(item.name) || capitalizeIngredientName(item.name);
  const rawA = effective ? effective.amount : item.amount;
  const unitForFormat = effective?.unit ?? item.unit;
  return formatShoppingListPurchaseLine({
    displayName: name,
    amount: rawA,
    unit: unitForFormat,
    mergeKey: item.meta?.merge_key,
    aggregationUnit: item.meta?.aggregation_unit,
    scaledDualDisplayAmount: effective?.scaledDualDisplayAmount ?? null,
    dualDisplayUnit: effective?.dualDisplayUnit ?? null,
  });
}

/** Фраза «в N рецепте» / «в N рецептах» для подписи в свёрнутой строке (N ≥ 2). */
function formatRecipeSourcesCountPhrase(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return `в ${n} рецепте`;
  return `в ${n} рецептах`;
}

/** Один пункт списка: чекбокс, название, количество, удалить; по тапу — раскрыть рецепты. */
function ShoppingListItem({
  item,
  recipeFilterSelectedIds,
  onTogglePurchased,
  onDelete,
}: {
  item: ShoppingListItemRow;
  /** Непустой Set — активен фильтр по рецептам; пересчёт количества и списка источников. */
  recipeFilterSelectedIds: Set<string> | null;
  onTogglePurchased: (itemId: string, is_purchased: boolean) => void;
  onDelete: (item: ShoppingListItemRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const view = useMemo(
    () => computeEffectiveShoppingItemView(item, recipeFilterSelectedIds),
    [item, recipeFilterSelectedIds]
  );
  const sources = view.sources;
  const hasSources = sources.length > 0;

  return (
    <motion.div
      className="rounded-lg border border-border/80 bg-card overflow-hidden"
      initial={false}
      animate={{ opacity: item.is_purchased ? 0.62 : 1 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-2 min-h-[44px]",
          hasSources && "cursor-pointer"
        )}
      >
        <motion.button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePurchased(item.id, !item.is_purchased);
          }}
          whileTap={{ scale: 0.92 }}
          animate={
            item.is_purchased ? { scale: [1, 1.1, 1] } : { scale: 1 }
          }
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            "w-5 h-5 rounded border shrink-0 flex items-center justify-center touch-manipulation overflow-hidden",
            item.is_purchased ? "bg-primary border-primary" : "border-border bg-background"
          )}
          aria-label={item.is_purchased ? "Отметить не купленным" : "Отметить купленным"}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {item.is_purchased && (
              <motion.span
                key="ok"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                transition={{ type: "spring", stiffness: 480, damping: 26 }}
                className="text-white text-xs leading-none"
              >
                ✓
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        <button
          type="button"
          onClick={() => hasSources && setExpanded((e) => !e)}
          className={cn(
            "flex-1 min-w-0 text-left flex items-center gap-2",
            !hasSources && "cursor-default"
          )}
        >
          <motion.span
            className="text-sm flex-1 min-w-0 block"
            animate={{ opacity: item.is_purchased ? 0.72 : 1 }}
            transition={{ duration: 0.2 }}
          >
            <span
              className={cn(
                "inline transition-[text-decoration-color] duration-200",
                item.is_purchased && "line-through decoration-muted-foreground/80 text-muted-foreground"
              )}
            >
              <span className="text-sm">
                {formatItemShort(item, { amount: view.amount, unit: view.unit })}
              </span>
              {!expanded && view.recipeCount > 1 && (
                <span className="text-xs text-muted-foreground">
                  {" "}
                  · {formatRecipeSourcesCountPhrase(view.recipeCount)}
                </span>
              )}
            </span>
          </motion.span>
          {hasSources && (
            <span className="shrink-0 text-muted-foreground">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
          className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 touch-manipulation"
          aria-label="Удалить из списка"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {expanded && hasSources && (
        <div className="px-3 pb-2 pt-0 border-t border-border/50 mt-0 bg-muted/30">
          <p className="text-xs text-muted-foreground mb-1">Из рецептов:</p>
          <ul className="text-xs space-y-0.5">
            {sources.map((r) => (
              <li key={r.id}>{r.title || "Без названия"}</li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

export function ShoppingListView() {
  const { toast } = useToast();
  const { selectedMemberId, members } = useFamily();
  const { hasAccess } = useSubscription();
  const memberId = mealPlanMemberIdForShoppingSync({ hasAccess, selectedMemberId, members });
  const [range, setRange] = useState<"today" | "week">("today");
  const [buildSheetOpen, setBuildSheetOpen] = useState(false);

  const {
    listId,
    listMeta,
    items,
    isLoading: listLoading,
    setItemPurchased,
    clearList,
    deleteItem,
    insertItem,
    removePurchased,
    adjustRecipeServingsInShoppingList,
    isAdjustingRecipeServings,
    bulkAdjustRecipeServingsInShoppingList,
    isBulkAdjustingRecipeServings,
  } = useShoppingList();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [addProductSheetOpen, setAddProductSheetOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | "all">("all");
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<Set<string>>(new Set());
  const [filterOnlyUnpurchased, setFilterOnlyUnpurchased] = useState(false);
  /** Выбранные рецепты в sheet фильтра; источник правды для чекбоксов (в т.ч. «Все рецепты» = полный набор id). */
  const [draftRecipeIds, setDraftRecipeIds] = useState<Set<string>>(() => new Set());
  const [recipeSearch, setRecipeSearch] = useState("");
  /** Значение степпера «для всех отмеченных» в sheet фильтра (инициализируется при открытии). */
  const [bulkServingsValue, setBulkServingsValue] = useState(4);
  const recipeFilterSheetWasOpenRef = useRef(false);
  const recipeFilterMasterCheckboxRef = useRef<HTMLInputElement>(null);

  const { data: planData, isLoading: planLoading } = usePlanShoppingIngredients(range, memberId);
  const planIngredients = planData?.ingredients ?? [];
  const planRecipeServingsMap = planData?.recipeServingsByRecipeId ?? {};

  const syncMetaStored = listMeta as ShoppingListSyncMeta | undefined;

  const handleCopy = () => {
    const recipeFilterSet = selectedRecipeIds.size > 0 ? selectedRecipeIds : null;
    const itemsForFormat = filteredItems.map((i) => {
      const v = computeEffectiveShoppingItemView(i, recipeFilterSet);
      return {
        name: normalizeIngredientDisplayName(i.name) || i.name,
        amount: v.amount,
        unit: v.unit,
        category: i.category,
        merge_key: i.meta?.merge_key ?? null,
        aggregation_unit: i.meta?.aggregation_unit ?? null,
        scaledDualDisplayAmount: v.scaledDualDisplayAmount,
        dualDisplayUnit: v.dualDisplayUnit,
      };
    });
    const text = formatShoppingListForCopy(itemsForFormat, range);
    navigator.clipboard?.writeText(text).then(
      () => toast({ title: "Скопировано" }),
      () => toast({ variant: "destructive", title: "Не удалось скопировать" })
    );
  };

  const handleClear = () => {
    clearList()
      .then(() => toast({ title: "Список очищен" }))
      .catch(() => toast({ variant: "destructive", title: "Не удалось очистить" }));
  };

  const handleRemovePurchased = () => {
    removePurchased()
      .then(() => toast({ title: "Купленные убраны" }))
      .catch(() => toast({ variant: "destructive", title: "Не удалось убрать" }));
  };

  const loading = listLoading || planLoading;
  const hasPlanData = (planIngredients?.length ?? 0) > 0;
  const emptyState = !loading && items.length === 0;

  const uniqueRecipes = useMemo(() => {
    const byId = new Map<string, SourceRecipe>();
    for (const item of items) {
      for (const r of getSourceRecipesFromItem(item)) {
        if (r.id && !byId.has(r.id)) byId.set(r.id, r);
      }
    }
    return [...byId.values()];
  }, [items]);

  /** Текущие порции по рецепту: снимок в meta или сумма из плана за выбранный период. */
  const servingsByRecipeId = useMemo(() => {
    const rs = syncMetaStored?.recipe_shopping_servings;
    const out: Record<string, number> = {};
    for (const r of uniqueRecipes) {
      const fromMeta = rs?.[r.id];
      if (typeof fromMeta === "number" && fromMeta >= 1) {
        out[r.id] = Math.round(fromMeta);
      } else {
        const fromPlan = planRecipeServingsMap[r.id];
        if (typeof fromPlan === "number" && fromPlan >= 1) {
          out[r.id] = Math.round(fromPlan);
        }
      }
    }
    return out;
  }, [uniqueRecipes, syncMetaStored, planRecipeServingsMap]);

  const handleRecipeServingsChange = (recipeId: string, next: number) => {
    const rs = syncMetaStored?.recipe_shopping_servings;
    const hasMeta = typeof rs?.[recipeId] === "number" && rs[recipeId] >= 1;
    const baselineServingsIfMissing = hasMeta ? undefined : planRecipeServingsMap[recipeId];
    adjustRecipeServingsInShoppingList({ recipeId, newServings: next, baselineServingsIfMissing }).catch(() => {
      toast({ variant: "destructive", title: "Не удалось пересчитать порции" });
    });
  };

  const canApplyBulkServings = useMemo(() => {
    const rs = syncMetaStored?.recipe_shopping_servings;
    for (const id of draftRecipeIds) {
      const fromMeta = typeof rs?.[id] === "number" && rs[id] >= 1;
      const fromPlan = typeof planRecipeServingsMap[id] === "number" && planRecipeServingsMap[id] >= 1;
      if (fromMeta || fromPlan) return true;
    }
    return false;
  }, [draftRecipeIds, syncMetaStored, planRecipeServingsMap]);

  const portionsBusy = isAdjustingRecipeServings || isBulkAdjustingRecipeServings;

  const handleBulkServingsChange = (next: number) => {
    const clamped = Math.max(1, Math.min(20, Math.round(next)));
    setBulkServingsValue(clamped);
    const ids = [...draftRecipeIds];
    if (ids.length === 0) {
      toast({ title: "Отметьте хотя бы один рецепт" });
      return;
    }
    const rs = syncMetaStored?.recipe_shopping_servings;
    const entries = ids
      .map((recipeId) => {
        const hasMeta = typeof rs?.[recipeId] === "number" && rs[recipeId] >= 1;
        return {
          recipeId,
          newServings: clamped,
          baselineServingsIfMissing: hasMeta ? undefined : planRecipeServingsMap[recipeId],
        };
      })
      .filter((e) => {
        const fromMeta = typeof rs?.[e.recipeId] === "number" && rs[e.recipeId] >= 1;
        const fromPlan =
          typeof planRecipeServingsMap[e.recipeId] === "number" && planRecipeServingsMap[e.recipeId] >= 1;
        return fromMeta || fromPlan;
      });
    if (entries.length === 0) {
      toast({
        title: "Нет данных о порциях",
        description:
          "Для отмеченных блюд нет снимка порций. Добавьте их из карточки рецепта или соберите список из меню.",
      });
      return;
    }
    bulkAdjustRecipeServingsInShoppingList(entries).catch(() => {
      toast({ variant: "destructive", title: "Не удалось применить порции" });
    });
  };

  /** При открытии sheet: черновик рецептов + стартовое значение массовых порций. */
  useEffect(() => {
    const wasOpen = recipeFilterSheetWasOpenRef.current;
    if (filterSheetOpen && !wasOpen) {
      const allIds = uniqueRecipes.map((r) => r.id);
      const nextDraft =
        selectedRecipeIds.size === 0 ? new Set(allIds) : new Set(selectedRecipeIds);
      setDraftRecipeIds(nextDraft);
      const vals = [...nextDraft]
        .map((id) => servingsByRecipeId[id])
        .filter((v): v is number => v != null);
      setBulkServingsValue(
        vals.length > 0
          ? Math.max(1, Math.min(20, Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)))
          : 4,
      );
    }
    recipeFilterSheetWasOpenRef.current = filterSheetOpen;
  }, [filterSheetOpen, uniqueRecipes, selectedRecipeIds, servingsByRecipeId]);

  const recipeFilterMasterChecked = useMemo(() => {
    if (uniqueRecipes.length === 0) return false;
    return (
      draftRecipeIds.size === uniqueRecipes.length &&
      uniqueRecipes.every((r) => draftRecipeIds.has(r.id))
    );
  }, [uniqueRecipes, draftRecipeIds]);

  const recipeFilterMasterIndeterminate = useMemo(() => {
    if (uniqueRecipes.length === 0) return false;
    return draftRecipeIds.size > 0 && !recipeFilterMasterChecked;
  }, [uniqueRecipes, draftRecipeIds, recipeFilterMasterChecked]);

  useEffect(() => {
    const el = recipeFilterMasterCheckboxRef.current;
    if (el) el.indeterminate = recipeFilterMasterIndeterminate;
  }, [recipeFilterMasterIndeterminate]);

  const recipeFilterApplyDisabled = uniqueRecipes.length > 0 && draftRecipeIds.size === 0;

  const handleRecipeFilterMasterChange = (checked: boolean) => {
    if (checked) {
      setDraftRecipeIds(new Set(uniqueRecipes.map((r) => r.id)));
    } else {
      setDraftRecipeIds(new Set());
    }
  };

  const filteredItems = useMemo(() => {
    let list = items;
    if (selectedCategory !== "all") {
      const cat = selectedCategory;
      list = list.filter((i) => {
        const normalized = mapDbProductCategoryToShoppingAisle(i.category as string | null);
        return normalized === cat;
      });
    }
    if (selectedRecipeIds.size > 0) {
      list = list.filter((i) => {
        const sources = getSourceRecipesFromItem(i);
        return sources.some((s) => selectedRecipeIds.has(s.id));
      });
    }
    if (filterOnlyUnpurchased) {
      list = list.filter((i) => !i.is_purchased);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(q));
    }
    return list;
  }, [items, selectedCategory, selectedRecipeIds, filterOnlyUnpurchased, searchQuery]);

  const byCategory = filteredItems.reduce((acc, item) => {
    const cat = mapDbProductCategoryToShoppingAisle(item.category as string | null);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {} as Record<ProductCategory, ShoppingListItemRow[]>);

  const visibleCategories = CATEGORY_ORDER.filter((c) => (byCategory[c]?.length ?? 0) > 0);

  const [listEntranceKey, setListEntranceKey] = useState(0);
  useEffect(() => {
    if (loading) return;
    if (filteredItems.length === 0) return;
    let armed = false;
    try {
      armed = sessionStorage.getItem(SHOPPING_LIST_ENTRANCE_SESSION_KEY) === "1";
      if (armed) sessionStorage.removeItem(SHOPPING_LIST_ENTRANCE_SESSION_KEY);
    } catch {
      /* private mode */
    }
    if (!armed) return;
    setListEntranceKey((k) => k + 1);
    const n = filteredItems.length + visibleCategories.length;
    const delayMs = Math.min(560, 240 + n * 22);
    const t = window.setTimeout(() => {
      try {
        navigator.vibrate?.(12);
      } catch {
        /* unsupported */
      }
    }, delayMs);
    return () => clearTimeout(t);
  }, [loading, filteredItems.length, visibleCategories.length]);

  const listStaggerVariants = useMemo(() => {
    const n = Math.max(filteredItems.length + visibleCategories.length, 8);
    const staggerItem = Math.min(0.034, Math.max(0.02, 0.52 / n));
    return {
      root: {
        hidden: {},
        show: {
          transition: { staggerChildren: 0.075, delayChildren: 0.045 },
        },
      },
      category: {
        hidden: {},
        show: {
          transition: {
            staggerChildren: staggerItem,
            delayChildren: 0.08,
          },
        },
      },
      categoryHeader: {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } },
      },
      row: {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } },
      },
    };
  }, [filteredItems.length, visibleCategories.length]);

  const filteredRecipesForSheet = useMemo(() => {
    if (!recipeSearch.trim()) return uniqueRecipes;
    const q = recipeSearch.trim().toLowerCase();
    return uniqueRecipes.filter((r) => (r.title ?? "").toLowerCase().includes(q));
  }, [uniqueRecipes, recipeSearch]);

  const handleApplyFilter = () => {
    if (recipeFilterApplyDisabled) return;
    const allIds = uniqueRecipes.map((r) => r.id);
    const allSelected =
      uniqueRecipes.length > 0 &&
      draftRecipeIds.size === uniqueRecipes.length &&
      allIds.every((id) => draftRecipeIds.has(id));

    if (allSelected || uniqueRecipes.length === 0) {
      setSelectedRecipeIds(new Set());
    } else {
      setSelectedRecipeIds(new Set(draftRecipeIds));
    }
    setFilterSheetOpen(false);
  };

  const handleResetFilters = () => {
    setSelectedCategory("all");
    setSelectedRecipeIds(new Set());
    setFilterOnlyUnpurchased(false);
    setDraftRecipeIds(new Set());
    setFilterSheetOpen(false);
  };

  const toggleDraftRecipe = (recipeId: string) => {
    setDraftRecipeIds((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };

  const handleDeleteItem = (item: ShoppingListItemRow) => {
    const sources = getSourceRecipesFromItem(item);
    const payload = {
      name: item.name,
      amount: item.amount,
      unit: item.unit,
      category: item.category ?? "other",
      source_recipes: sources.length ? sources : undefined,
    };
    deleteItem(item.id)
      .then(() => {
        const t = toast({
          title: "Удалено",
          action: (
            <ToastAction
              altText="Отменить"
              onClick={() => {
                insertItem(payload).catch(() => toast({ variant: "destructive", title: "Не удалось вернуть" }));
              }}
            >
              Отменить
            </ToastAction>
          ),
        });
        setTimeout(() => t.dismiss(), 4000);
      })
      .catch(() => toast({ variant: "destructive", title: "Не удалось удалить" }));
  };

  const filterActiveCount = [selectedCategory !== "all", selectedRecipeIds.size > 0, filterOnlyUnpurchased].filter(
    Boolean
  ).length;

  const listHasContent = items.length > 0;

  const periodHeadline =
    range === "today" ? "Список покупок на сегодня" : "Список покупок на неделю";
  const periodSubline = listHasContent
    ? null
    : syncMetaStored?.last_synced_at
      ? "Соберите снова из плана или добавьте вручную."
      : "Соберите из плана или добавьте продукты вручную.";

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground tracking-tight">{periodHeadline}</h2>
        {periodSubline != null && (
          <p className="text-xs text-muted-foreground mt-1">{periodSubline}</p>
        )}
        <p className="text-[11px] leading-snug text-muted-foreground mt-2 max-w-lg">{PANTRY_ASSUMPTION_USER_HINT_RU}</p>
      </div>

      <BuildShoppingListFromPlanSheet
        open={buildSheetOpen}
        onOpenChange={setBuildSheetOpen}
        planMemberId={memberId}
        hasAccess={hasAccess}
      />

      {/* Верхний ряд: Сегодня/Неделя + селектор профиля */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-full border border-border overflow-hidden bg-muted/30 shrink-0">
          <button
            type="button"
            onClick={() => setRange("today")}
            className={cn(
              "px-3 py-2 text-[13px] font-medium transition-colors",
              range === "today" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Сегодня
          </button>
          <button
            type="button"
            onClick={() => setRange("week")}
            className={cn(
              "px-3 py-2 text-[13px] font-medium transition-colors",
              range === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Неделя
          </button>
        </div>
        <MemberSelectorButton className="shrink-0 ml-auto" />
      </div>

      {/* Поиск, фильтр и меню — одна строка, если список не пустой; иначе только «Ещё» */}
      {!loading && (
        <div className={cn("flex items-center gap-2", items.length === 0 && "justify-end")}>
          {items.length > 0 && (
            <>
              <Input
                type="search"
                placeholder="Поиск…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 flex-1 min-w-0 text-sm"
                aria-label="Поиск по ингредиентам"
              />
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-9 shrink-0 gap-1.5 px-2.5 text-muted-foreground font-normal hover:text-foreground",
                  filterActiveCount > 0 && "text-primary hover:text-primary/80"
                )}
                onClick={() => setFilterSheetOpen(true)}
              >
                <Filter className="w-3.5 h-3.5 opacity-80" />
                <span className="text-xs">Фильтр</span>
                {filterActiveCount > 0 && <span className="text-xs font-medium">({filterActiveCount})</span>}
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 w-9 shrink-0 p-0 text-muted-foreground">
                <MoreVertical className="w-4 h-4" aria-label="Ещё" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleCopy} disabled={items.length === 0}>
                <Copy className="w-3.5 h-3.5 mr-2" />
                Скопировать список
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddProductSheetOpen(true)}>
                <ListPlus className="w-3.5 h-3.5 mr-2" />
                Добавить продукт вручную
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleRemovePurchased} disabled={!items.some((i) => i.is_purchased)}>
                Убрать купленные
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleClear} disabled={items.length === 0} className="text-destructive focus:text-destructive">
                Очистить список
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Filter sheet */}
      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] flex flex-col p-0">
          <SheetHeader className="p-5 pb-3 text-left space-y-1">
            <SheetTitle className="text-base">Фильтр</SheetTitle>
            <SheetDescription className="sr-only">Настройка отображения списка покупок</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-5 space-y-6 pb-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2.5">Категория</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory("all")}
                  className={cn(
                    "px-2.5 py-1.5 text-xs font-medium rounded-full border",
                    selectedCategory === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-muted/30"
                  )}
                >
                  Все
                </button>
                {CATEGORY_ORDER.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={cn(
                      "px-2.5 py-1.5 text-xs font-medium rounded-full border",
                      selectedCategory === cat ? "bg-primary text-primary-foreground border-primary" : "border-border bg-muted/30"
                    )}
                  >
                    {CATEGORY_LABEL[cat]}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border/50 bg-muted/20 px-3 py-3">
              <input
                type="search"
                placeholder="Поиск по рецепту…"
                value={recipeSearch}
                onChange={(e) => setRecipeSearch(e.target.value)}
                className="flex h-8 w-full rounded-md border border-border/60 bg-background/80 px-2.5 py-1 text-xs mb-2.5"
              />
              <div className="flex items-center gap-2 py-1 min-h-9">
                <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                  <input
                    ref={recipeFilterMasterCheckboxRef}
                    type="checkbox"
                    checked={recipeFilterMasterChecked}
                    onChange={(e) => handleRecipeFilterMasterChange(e.target.checked)}
                    className="h-4 w-4 rounded border-border shrink-0"
                  />
                  <span className="text-sm text-foreground/90">Все рецепты</span>
                </label>
                <div
                  className="w-[108px] shrink-0 flex justify-end"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  {draftRecipeIds.size > 0 && canApplyBulkServings ? (
                    <div
                      className="inline-flex items-center rounded-full bg-primary-light/50 border border-primary-border/70 overflow-hidden shrink-0"
                      role="group"
                      aria-label="Порции для отмеченных рецептов"
                    >
                      <motion.button
                        type="button"
                        disabled={portionsBusy || bulkServingsValue <= 1}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleBulkServingsChange(bulkServingsValue - 1);
                        }}
                        whileTap={{ scale: 0.96 }}
                        className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 text-base leading-none font-medium touch-manipulation"
                        aria-label="Уменьшить порции у отмеченных"
                      >
                        −
                      </motion.button>
                      <span className="min-w-[1.25rem] px-0.5 text-center text-xs font-semibold text-foreground tabular-nums">
                        {bulkServingsValue}
                      </span>
                      <motion.button
                        type="button"
                        disabled={portionsBusy || bulkServingsValue >= 20}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleBulkServingsChange(bulkServingsValue + 1);
                        }}
                        whileTap={{ scale: 0.96 }}
                        className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 text-base leading-none font-medium touch-manipulation"
                        aria-label="Увеличить порции у отмеченных"
                      >
                        +
                      </motion.button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground tabular-nums w-full text-right pr-0.5" title="Отметьте рецепты с известными порциями">
                      —
                    </span>
                  )}
                </div>
              </div>
              <ul className="max-h-44 overflow-y-auto space-y-1 mt-1">
                {filteredRecipesForSheet.map((r) => {
                  const sv = servingsByRecipeId[r.id];
                  const canPortions = sv != null;
                  return (
                    <li key={r.id} className="flex items-center gap-2 py-1 min-h-9">
                      <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={draftRecipeIds.has(r.id)}
                          onChange={() => toggleDraftRecipe(r.id)}
                          className="h-4 w-4 rounded border-border shrink-0"
                        />
                        <span className="text-sm text-foreground/85 truncate min-w-0" title={r.title || undefined}>
                          {r.title || "Без названия"}
                        </span>
                      </label>
                      <div
                        className="w-[108px] shrink-0 flex justify-end"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        {canPortions ? (
                          <div
                            className="inline-flex items-center rounded-full bg-primary-light/50 border border-primary-border/70 overflow-hidden shrink-0"
                            role="group"
                            aria-label={`Порции: ${r.title || "Рецепт"}`}
                          >
                            <motion.button
                              type="button"
                              disabled={portionsBusy || sv <= 1}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRecipeServingsChange(r.id, sv - 1);
                              }}
                              whileTap={{ scale: 0.96 }}
                              className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 text-base leading-none font-medium touch-manipulation"
                              aria-label="Уменьшить порции"
                            >
                              −
                            </motion.button>
                            <span className="min-w-[1.25rem] px-0.5 text-center text-xs font-semibold text-foreground tabular-nums">
                              {sv}
                            </span>
                            <motion.button
                              type="button"
                              disabled={portionsBusy || sv >= 20}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRecipeServingsChange(r.id, sv + 1);
                              }}
                              whileTap={{ scale: 0.96 }}
                              className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 text-base leading-none font-medium touch-manipulation"
                              aria-label="Увеличить порции"
                            >
                              +
                            </motion.button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground tabular-nums w-full text-right pr-0.5">—</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <label className="flex items-center gap-2.5 py-1 cursor-pointer min-h-10">
              <input
                type="checkbox"
                checked={filterOnlyUnpurchased}
                onChange={(e) => setFilterOnlyUnpurchased(e.target.checked)}
                className="h-4 w-4 rounded border-border shrink-0"
              />
              <span className="text-sm">Только некупленные</span>
            </label>
          </div>
          <SheetFooter className="p-5 pt-3 border-t flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground py-2 sm:py-0 self-center sm:self-auto"
              onClick={handleResetFilters}
            >
              Сбросить фильтры
            </button>
            <Button
              className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleApplyFilter}
              disabled={recipeFilterApplyDisabled}
              title={
                recipeFilterApplyDisabled ? "Выберите хотя бы один рецепт или отметьте «Все рецепты»" : undefined
              }
            >
              Применить
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Add product sheet */}
      <AddProductSheet
        open={addProductSheetOpen}
        onOpenChange={setAddProductSheetOpen}
        onAdd={async (fields) => {
          await insertItem({
            name: fields.name.trim(),
            amount: fields.amount ?? null,
            unit: fields.unit?.trim() || null,
            category: fields.category ?? "other",
          });
          setAddProductSheetOpen(false);
          toast({ title: "Продукт добавлен" });
        }}
        onError={() => toast({ variant: "destructive", title: "Не удалось добавить" })}
      />

      {loading && (
        <div className="py-8 text-center text-sm text-muted-foreground">Загрузка…</div>
      )}

      {emptyState && (
        <div className="rounded-2xl border border-border bg-card shadow-soft p-8 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            {hasPlanData
              ? "Соберите список из текущего меню — ингредиенты с разных блюд объединятся."
              : "Добавьте блюда в План или начните с пустого списка и добавьте продукты вручную."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-stretch">
            <Button
              size="sm"
              className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => setBuildSheetOpen(true)}
            >
              <ListPlus className="w-3.5 h-3.5" />
              Собрать из меню
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAddProductSheetOpen(true)}>
              <ListPlus className="w-3.5 h-3.5" />
              Добавить вручную
            </Button>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className="space-y-5">
            {filteredItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">По выбранным фильтрам ничего не найдено.</p>
            ) : (
              <motion.div
                key={listEntranceKey}
                className="space-y-5"
                initial={listEntranceKey > 0 ? "hidden" : false}
                animate="show"
                variants={listStaggerVariants.root}
              >
                {visibleCategories.map((cat) => (
                  <motion.section
                    key={cat}
                    variants={listStaggerVariants.category}
                    className="block space-y-2"
                  >
                    <motion.h3
                      variants={listStaggerVariants.categoryHeader}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground tracking-wide mb-2.5"
                    >
                      <IconBadge icon={CATEGORY_ICON[cat]} variant={CATEGORY_BADGE_VARIANT[cat]} size="sm" />
                      <span className="text-foreground">{CATEGORY_LABEL[cat]}</span>
                    </motion.h3>
                    <div className="space-y-2">
                      {byCategory[cat].map((item) => (
                        <motion.div key={item.id} variants={listStaggerVariants.row}>
                          <ShoppingListItem
                            item={item}
                            recipeFilterSelectedIds={
                              selectedRecipeIds.size > 0 ? selectedRecipeIds : null
                            }
                            onTogglePurchased={(id, is_purchased) => setItemPurchased({ itemId: id, is_purchased })}
                            onDelete={(it) => handleDeleteItem(it)}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.section>
                ))}
              </motion.div>
            )}
          </div>
          {filteredItems.length > 0 && (
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-2 -mt-1"
            >
              <Copy className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
              Скопировать список
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Sheet для ручного добавления продукта. */
function AddProductSheet({
  open,
  onOpenChange,
  onAdd,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (fields: { name: string; amount: number | null; unit: string | null; category: ProductCategory }) => Promise<void>;
  onError: () => void;
}) {
  const [name, setName] = useState("");
  const [amountStr, setAmountStr] = useState("");
  const [unit, setUnit] = useState("");
  const [category, setCategory] = useState<ProductCategory | "">("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const amount = amountStr.trim() ? parseFloat(amountStr) : null;
    const num = amount != null && Number.isFinite(amount) ? amount : null;
    try {
      await onAdd({
        name: trimmed,
        amount: num,
        unit: unit.trim() || null,
        category: (category as ProductCategory) || "other",
      });
      setName("");
      setAmountStr("");
      setUnit("");
      setCategory("");
    } catch {
      onError();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Добавить продукт</SheetTitle>
          <SheetDescription>Название обязательно; количество и категория — по желанию.</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Название</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Молоко"
              className="w-full"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Количество</label>
              <Input
                type="number"
                step="any"
                min="0"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="—"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Единица</label>
              <Input
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="г, мл, шт."
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Категория</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ProductCategory | "")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {CATEGORY_ORDER.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </div>
          <SheetFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" disabled={!name.trim()}>
              Добавить
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
